import React, { useEffect, useRef, useState } from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { ErrorBoundary, useErrorBoundary } from 'react-error-boundary';
import type { FallbackProps } from 'react-error-boundary';
import { HotkeysProvider } from 'react-hotkeys-hook';
import { useTranslation } from 'react-i18next';
import { BrowserRouter } from 'react-router';

import { styles } from '@actual-app/components/styles';
import { View } from '@actual-app/components/view';
import {
  init as initConnection,
  send,
} from '@actual-app/core/platform/client/connection';
import { useQueryClient } from '@tanstack/react-query';

import { setAppState, sync } from '#app/appSlice';
import {
  closeBudget,
  loadAllFiles,
  loadBudget,
} from '#budgetfiles/budgetfilesSlice';
import { handleGlobalEvents } from '#global-events';
import { useIsTestEnv } from '#hooks/useIsTestEnv';
import { useMetadataPref } from '#hooks/useMetadataPref';
import { useOnVisible } from '#hooks/useOnVisible';
import { SpreadsheetProvider } from '#hooks/useSpreadsheet';
import { setI18NextLanguage } from '#i18n';
import { addNotification } from '#notifications/notificationsSlice';
import { loadGlobalPrefs, loadPrefs } from '#prefs/prefsSlice';
import { useDispatch, useSelector, useStore } from '#redux';
import {
  CustomThemeStyle,
  hasHiddenScrollbars,
  ThemeStyle,
  useTheme,
} from '#style';
import { getUserData, signOut } from '#users/usersSlice';
import { ExposeNavigate } from '#util/router-tools';

import { AppBackground } from './AppBackground';
import { BudgetMonthCountProvider } from './budget/BudgetMonthCountContext';
import { AriaRouterProvider } from './common/AriaRouterProvider';
import { DevelopmentTopBar } from './DevelopmentTopBar';
import { FatalError } from './FatalError';
import { FinancesApp } from './FinancesApp';
import { ManagementApp } from './manager/ManagementApp';
import { Modals } from './Modals';
import { SidebarProvider } from './sidebar/SidebarProvider';
import { UpdateNotification } from './UpdateNotification';

type RemoteStartupConfig = {
  budgetPassword?: string;
  serverPassword?: string;
  serverURL: string;
  syncId: string;
};

function getRemoteStartupConfig(): RemoteStartupConfig | null {
  if (process.env.NODE_ENV !== 'development') {
    return null;
  }

  const serverURL = process.env.ACTUAL_SERVER_URL;
  const syncId = process.env.ACTUAL_SYNC_ID;

  if (!serverURL || !syncId) {
    return null;
  }

  return {
    serverURL,
    syncId,
    serverPassword: process.env.ACTUAL_PASSWORD || undefined,
    budgetPassword: process.env.ACTUAL_BUDGET_PASSWORD || undefined,
  };
}
function AppInner() {
  const [budgetId] = useMetadataPref('id');
  const [cloudFileId] = useMetadataPref('cloudFileId');
  const { t } = useTranslation();
  const { showBoundary: showErrorBoundary } = useErrorBoundary();
  const dispatch = useDispatch();
  const userData = useSelector(state => state.user.data);

  useEffect(() => {
    setI18NextLanguage(null);
  }, []);

  const isInitialized = useRef(false);
  useEffect(() => {
    if (isInitialized.current) {
      return;
    }
    isInitialized.current = true;

    const maybeUpdate = async <T,>(cb?: () => T): Promise<T | void> => {
      if (global.Actual.isUpdateReadyForDownload()) {
        dispatch(
          setAppState({
            loadingText: t('Downloading and applying update...'),
          }),
        );
        await global.Actual.applyAppUpdate();
      }
      return cb?.();
    };

    async function maybeLoadRemoteBudget() {
      const config = getRemoteStartupConfig();
      if (!config) {
        return false;
      }

      dispatch(
        setAppState({
          loadingText: t('Connecting to remote Actual sync server...'),
        }),
      );

      const currentServerURL = await send('get-server-url');
      if (currentServerURL !== config.serverURL) {
        const { error } = await send('set-server-url', {
          url: config.serverURL,
          validate: true,
        });

        if (error) {
          throw new Error(`Could not configure remote server: ${error}`);
        }
      }

      const currentUser = await send('subscribe-get-user');
      if (!currentUser || currentUser.offline || currentUser.tokenExpired) {
        if (!config.serverPassword) {
          throw new Error(
            'ACTUAL_PASSWORD is required to sign in before pulling the remote budget.',
          );
        }

        const { error } = await send('subscribe-sign-in', {
          password: config.serverPassword,
          loginMethod: 'password',
        });

        if (error) {
          throw new Error(`Could not sign in to remote server: ${error}`);
        }
      }

      dispatch(
        setAppState({
          loadingText: t('Pulling the latest remote budget data...'),
        }),
      );

      await send('api/download-budget', {
        syncId: config.syncId,
        password: config.budgetPassword,
      });
      await dispatch(loadPrefs());
      await dispatch(getUserData());
      await dispatch(loadAllFiles());
      return true;
    }

    async function init() {
      await maybeUpdate();
      dispatch(
        setAppState({
          loadingText: t(
            'Initializing the connection to the local database...',
          ),
        }),
      );
      await initConnection();

      // Load any global prefs
      dispatch(
        setAppState({
          loadingText: t('Loading global preferences...'),
        }),
      );
      await dispatch(loadGlobalPrefs());

      try {
        dispatch(
          setAppState({
            loadingText: t('Checking remote startup sync...'),
          }),
        );
        const didLoadRemoteBudget = await maybeLoadRemoteBudget();
        if (didLoadRemoteBudget) {
          await maybeUpdate();
          return;
        }
      } catch (error) {
        console.error('Failed to pull the remote budget on startup:', error);
        dispatch(
          addNotification({
            notification: {
              type: 'error',
              id: 'remote-startup-sync-failed',
              title: t('Remote startup sync failed'),
              message:
                error instanceof Error
                  ? error.message
                  : t('Unknown startup sync error'),
            },
          }),
        );
      }

      // Open the last opened budget, if any
      dispatch(
        setAppState({
          loadingText: t('Opening last budget...'),
        }),
      );
      const budgetId = await send('get-last-opened-backup');
      if (budgetId) {
        await dispatch(loadBudget({ id: budgetId }));

        // Check to see if this file has been remotely deleted (but
        // don't block on this in case they are offline or something)
        dispatch(
          setAppState({
            loadingText: t('Retrieving remote files...'),
          }),
        );

        const files = await send('get-remote-files');
        if (files) {
          const remoteFile = files.find(f => f.fileId === cloudFileId);
          if (remoteFile && remoteFile.deleted) {
            void dispatch(closeBudget());
          }
        }

        await maybeUpdate();
      }
    }

    async function initAll() {
      await init();
      dispatch(setAppState({ loadingText: null }));
    }

    initAll().catch(showErrorBoundary);
  }, [cloudFileId, dispatch, showErrorBoundary, t]);

  useEffect(() => {
    if (userData?.tokenExpired) {
      dispatch(
        addNotification({
          notification: {
            type: 'error',
            id: 'login-expired',
            title: t('Login expired'),
            sticky: true,
            message: t('Login expired, please log in again.'),
            button: {
              title: t('Go to login'),
              action: () => {
                void dispatch(signOut());
              },
            },
          },
        }),
      );
    }
  }, [dispatch, t, userData?.tokenExpired]);

  return budgetId ? <FinancesApp /> : <ManagementApp />;
}

function ErrorFallback({ error }: FallbackProps) {
  const dispatch = useDispatch();

  // If startup failed mid-way, a loading message is still set. That hides
  // every modal (see modalsSlice), including the FatalError one, so clear it.
  useEffect(() => {
    dispatch(setAppState({ loadingText: null }));
  }, [dispatch]);

  return (
    <>
      <AppBackground />
      <FatalError error={error} />
    </>
  );
}

export function App() {
  const store = useStore();
  const isTestEnv = useIsTestEnv();
  const queryClient = useQueryClient();

  useEffect(() => handleGlobalEvents(store, queryClient), [store, queryClient]);

  const [hiddenScrollbars, setHiddenScrollbars] = useState(
    hasHiddenScrollbars(),
  );
  const dispatch = useDispatch();

  useOnVisible(async () => {
    console.debug('triggering sync because of visibility change');
    await dispatch(sync());
  });

  useEffect(() => {
    function checkScrollbars() {
      if (hiddenScrollbars !== hasHiddenScrollbars()) {
        setHiddenScrollbars(hasHiddenScrollbars());
      }
    }

    window.addEventListener('focus', checkScrollbars);
    return () => window.removeEventListener('focus', checkScrollbars);
  }, [hiddenScrollbars]);

  const [theme] = useTheme();

  return (
    <BrowserRouter>
      <ExposeNavigate />
      <AriaRouterProvider>
        <HotkeysProvider initiallyActiveScopes={['app']}>
          <SpreadsheetProvider>
            <SidebarProvider>
              <BudgetMonthCountProvider>
                <DndProvider backend={HTML5Backend}>
                  <View
                    data-theme={theme}
                    style={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                  >
                    <View
                      key={
                        hiddenScrollbars ? 'hidden-scrollbars' : 'scrollbars'
                      }
                      style={{
                        flexGrow: 1,
                        overflow: 'hidden',
                        ...styles.lightScrollbar,
                      }}
                    >
                      <ErrorBoundary FallbackComponent={ErrorFallback}>
                        {import.meta.env.REACT_APP_REVIEW_ID && !isTestEnv && (
                          <DevelopmentTopBar />
                        )}
                        <AppInner />
                      </ErrorBoundary>
                      <ThemeStyle />
                      <CustomThemeStyle />
                      <ErrorBoundary FallbackComponent={FatalError}>
                        <Modals />
                      </ErrorBoundary>
                      <UpdateNotification />
                    </View>
                  </View>
                </DndProvider>
              </BudgetMonthCountProvider>
            </SidebarProvider>
          </SpreadsheetProvider>
        </HotkeysProvider>
      </AriaRouterProvider>
    </BrowserRouter>
  );
}
